import { formatISODate } from '@utils/date';

import { FormActivityActionType, FormActivityChange } from '../types';
import {
  FormActivityFieldMetadataApi,
  FormActivityModifiedDataApi
} from '../types/formActivityLogApi';

const ACTIVITY_FIELD_LABELS: Record<string, string> = {
  name: 'Changed form name',
  form_name: 'Changed form name',
  description: 'Changed description',
  add_to: 'Changed add-to group',
  registration_end_time: 'Changed form close date',
  source: 'Changed source tracking',
  is_source_tracking_enabled: 'Source tracking enabled',
  tuition_fee: 'Updated Tuition fee',
  fee: 'Updated Tuition fee',
  fee_amount: 'Updated fee amount',
  image: 'Updated cover image',
  image_url: 'Updated cover image',
  cover_image: 'Updated cover image',
  publish: 'Form published',
  form_published: 'Form published',
  status: 'Status updated'
};

const FIELD_CHANGE_LABELS: Record<
  FormActivityFieldMetadataApi['change'],
  string
> = {
  FIELD_ADDED: 'Field added',
  FIELD_REMOVED: 'Field removed',
  ORDER_CHANGED: 'Field order changed',
  MARKED_REQUIRED: 'Field marked required',
  MARKED_OPTIONAL: 'Field marked optional'
};

const PUBLISH_FIELDS = new Set(['publish', 'form_published']);

const DATE_ACTIVITY_FIELDS = new Set(['registration_end_time']);

const HTML_ACTIVITY_FIELDS = new Set(['description']);

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}/;

const isHtmlActivityField = (field: string) =>
  HTML_ACTIVITY_FIELDS.has(field?.toLowerCase() || '');

export const formatActivityLogDisplayDate = (value: string): string =>
  formatISODate({
    date: value,
    format: 'dd-mm-yyyy',
    separator: '/'
  });

const isIsoDateValue = (value: string) => {
  if (!ISO_DATE_PATTERN.test(value)) {
    return false;
  }

  const parsed = new Date(value);
  return !Number.isNaN(parsed.getTime());
};

const formatActivityLogValue = (field: string, value: string): string => {
  if (!value) {
    return value;
  }

  const normalizedField = field?.toLowerCase() || '';

  const shouldFormatDate =
    DATE_ACTIVITY_FIELDS.has(normalizedField) || isIsoDateValue(value);

  if (!shouldFormatDate) {
    return value;
  }

  return formatActivityLogDisplayDate(value) || value;
};

export const formatActivityLogDate = (timestamp: string): string => {
  const formatted = formatActivityLogDisplayDate(timestamp);
  return formatted ? `on ${formatted}` : '';
};

export const getActivityLogTitle = (
  actionType: FormActivityActionType,
  version: number
): string => {
  if (actionType === 'CREATE') {
    return version === 1
      ? `V${version} (version ${version}) created`
      : `V${version} created`;
  }
  if (actionType === 'PUBLISH') {
    return version === 1
      ? `V${version} (version ${version}) published`
      : `V${version} published`;
  }
  if (actionType === 'STATUS_UPDATE') {
    return `V${version} status updated`;
  }
  return `V${version} updated`;
};

export const getVersionGroupTitle = (
  version: number,
  items: { actionType: FormActivityActionType; title: string }[]
): string => {
  if (items.length === 1) {
    return items[0].title;
  }

  const actionTypes = new Set(items.map((item) => item.actionType));
  if (actionTypes.has('CREATE')) {
    return version === 1
      ? `V${version} (version ${version}) created`
      : `V${version} created`;
  }
  if (actionTypes.has('PUBLISH') && !actionTypes.has('UPDATE')) {
    return version === 1
      ? `V${version} (version ${version}) published`
      : `V${version} published`;
  }
  if (actionTypes.has('STATUS_UPDATE') && actionTypes.size === 1) {
    return `V${version} status updated`;
  }

  return `V${version} updated`;
};

export const getActivityFieldLabel = (field: string): string => {
  const normalized = field?.toLowerCase() || '';
  return (
    ACTIVITY_FIELD_LABELS[normalized] ??
    normalized
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  );
};

export const formatActivityChangeSummary = (
  field: string,
  previousValue: string | null | undefined,
  currentValue: string | null | undefined
): string => {
  const previous = formatActivityLogValue(field, previousValue ?? '');
  const current = formatActivityLogValue(field, currentValue ?? '');

  if (PUBLISH_FIELDS.has(field?.toLowerCase() || '')) {
    return current || previous;
  }

  if (previous && current) {
    return `'${previous}' → '${current}'`;
  }

  if (current) {
    return `'${current}'`;
  }

  if (previous) {
    return `'${previous}' → removed`;
  }

  return '';
};

export const formatActivityFieldChangeSummary = (
  metadata: FormActivityFieldMetadataApi
): string => {
  if (metadata.change === 'ORDER_CHANGED') {
    return '';
  }

  return `'${metadata.field_name}'`;
};

export const mapFieldMetadataToChange = (
  metadata: FormActivityFieldMetadataApi
): FormActivityChange => ({
  label: FIELD_CHANGE_LABELS[metadata.change],
  summary: formatActivityFieldChangeSummary(metadata)
});

export const mapCustomFieldSyncMetadataToChanges = (
  metadata: FormActivityFieldMetadataApi[]
): FormActivityChange[] => {
  const removed = metadata.filter((m) => m.change === 'FIELD_REMOVED');
  const addedByOrder = new Map(
    metadata
      .filter((m) => m.change === 'FIELD_ADDED')
      .map((m) => [m.display_order, m])
  );

  return removed.map((removedField) => {
    const addedField = addedByOrder.get(removedField.display_order);
    if (removedField.field_name === addedField?.field_name) {
      return {
        label: 'Updated dropdown options',
        summary: `'${removedField.field_name}'`
      };
    }
    if (addedField) {
      return {
        label: 'Changed custom field name',
        summary: formatActivityChangeSummary(
          'form_field',
          removedField.field_name,
          addedField.field_name
        )
      };
    }
    return mapFieldMetadataToChange(removedField);
  });
};

const mapHtmlFieldToChange = (
  entry: FormActivityModifiedDataApi
): FormActivityChange => ({
  label: getActivityFieldLabel(entry.field),
  summary: '',
  richTextSummary: {
    previous: entry.previous_value ?? undefined,
    current: entry.current_value ?? undefined
  }
});

export const mapModifiedDataToChanges = (
  entry: FormActivityModifiedDataApi
): FormActivityChange[] => {
  if (entry.field === 'form_field' && entry.metadata?.length) {
    return entry.metadata.map(mapFieldMetadataToChange);
  }

  if (isHtmlActivityField(entry.field)) {
    return [mapHtmlFieldToChange(entry)];
  }

  return [
    {
      label: getActivityFieldLabel(entry.field),
      summary: formatActivityChangeSummary(
        entry.field,
        entry.previous_value,
        entry.current_value
      )
    }
  ];
};

export const getActivityChangeCount = (
  modifiedData: FormActivityModifiedDataApi[]
): number =>
  modifiedData.reduce((count, entry) => {
    if (PUBLISH_FIELDS.has(entry.field?.toLowerCase() || '')) {
      return count;
    }
    if (entry.field === 'form_field' && entry.metadata?.length) {
      return count + entry.metadata.length;
    }
    return count + 1;
  }, 0);
